import { ActionEvent, LayoutType, Mode } from '@/types';
import { provide } from '@lit/context';
import { LitElement, PropertyValueMap } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, Ref } from 'lit/directives/ref.js';
import { ParsedQs } from 'qs';

import {
  CancelablePromise,
  CancelError,
  ConfigurationDto,
  ConfiguratorDto,
  ConfiguratorLayout,
  ConfiguratorService,
  Step,
  StepDto,
} from '@/api-sdk';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleType,
} from '@/context';
import {
  GlobalValidationConfiguratorContext,
  initValidationConfiguratorContext,
  ValidationConfiguratorContextType,
} from '@/context/context.validation.configurator';
import {
  MobileController,
  PersonalisationEventController,
  PrintExampleEventController,
} from '@/controllers';
import {
  exposeComponentChange,
  getHspEnabled,
  getParamsObjectFromQueryString,
  i18nInit,
  setBaseUrl,
  updateValidationConfiguratorContext,
} from '@/utils';
import { goToStep, toggleMobileConfigurator } from './utils';

import { FgConfigurator } from '@/components';

type Constructor<T> = { new (...args: any[]): T };

export const ConfiguratorMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class ConfiguratorMixinClass extends superClass {
    private static readonly LOADINGSPINNER_DELAY = 250;
    private static readonly QUANTITY_STEPS = [
      Step.QUANTITY,
      Step.QUANTITY_PER_COLOR,
      Step.QUANTITY_PER_SIZE,
      Step.QUANTITY_PER_SIZE_PER_COLOR,
      Step.QUANTITY_PER_VARIANT,
    ];

    @property({ type: String })
    product = '-1';

    @property({ type: String })
    productName?: string;

    @property({ type: String })
    productImage?: string;

    @property({ type: String })
    locale = 'en_US';

    @property({ type: String })
    color = '';

    @property({ type: String })
    baseUrl = '';

    @property({ type: String })
    configurationId?: string;

    @property({ type: Array })
    usps: { text: string }[] = [];

    @property({ type: Boolean })
    editing = false;

    @property({ type: String })
    layout: LayoutType = 'wholesale';

    /**
     * Debtor is the loggedin reseller ID from CE
     *
     * @property {string} debtor
     */
    @property({ type: String })
    debtor: string | undefined = undefined;

    /**
     * Country is the country from the loggedin reseller from CE
     *
     * @property {string} country
     */
    @property({ type: String })
    country: string | undefined = undefined;

    /**
     * Property which can be set in CE to determine if the product is salable or not
     * depends on the Magento salable return
     */
    @property({ type: Boolean })
    salable = false;

    /**
     * If customer in logged in this value should be true
     */
    @property({ type: Boolean })
    loggedIn = false;

    /**
     * Customer ID is the loggedin customer ID from CE
     */
    @property({ type: Number })
    customerId?: number;

    /**
     * Used to make some links inside of web components that need to link to store specific urls
     */
    @property({ type: String })
    shopUrl = '';

    @property({ type: Number })
    stickySummaryHeight = 0;

    @state()
    steps: StepDto[] = [];

    @state()
    configuration?: ConfigurationDto | null;

    @state()
    queryString = '';

    @state()
    isValidConfiguration = false;

    @state()
    isLoading = true;

    @state()
    error = false;

    getGetConfiguratorPromise: CancelablePromise<ConfiguratorDto> | null = null;

    @state()
    isConfiguratorLoaded = false;

    activeTimeoutIDs: NodeJS.Timeout[] = [];

    @state()
    validSteps: { [key: string]: boolean } = {};

    @state()
    quantityValidated = false;

    @state()
    firstValidationMessage: string | null = '';

    @state()
    activeStep = 0;

    @state()
    toggleMobileConfigurator = false;

    @state()
    unprinted = false;

    @state()
    selectedColor?: {
      image: string;
      color: string;
      selected: boolean;
      thumbImage: string;
    };

    @state()
    eventToSave?: string;

    @state()
    mode: Mode = Mode.DEFAULT;

    @state()
    @provide({ context: global.context })
    globalContext!: GlobalComponentType;

    @state()
    @provide({ context: configuratorGlobal.context })
    configuratorContext!: ConfiguratorContextType;

    @state()
    @provide({ context: configuratorGlobal.resetContext })
    resetConfigurator = false;

    @state()
    @provide({ context: GlobalValidationConfiguratorContext })
    validationConfiguratorContext!: ValidationConfiguratorContextType;

    @state()
    @provide({ context: personalisation.context })
    personalisationContext: PersonalisationType[] = [];

    @state()
    @provide({ context: printExample.context })
    printExampleContext?: PrintExampleType;

    private readonly VATRATE = '0.2100';
    private _vatRate = this.VATRATE;
    set vatRate(val: string) {
      // always fallback to 21% VAT
      this._vatRate = val || this.VATRATE;
    }

    @property({ type: String })
    get vatRate() {
      return this._vatRate;
    }

    protected mobileController = new MobileController(this);

    protected peronsalisationEventController =
      new PersonalisationEventController(this, this.personalisationContext);

    protected printExampleEventController = new PrintExampleEventController(
      this,
      this.printExampleContext,
    );

    public configuratorElement: Ref<HTMLElement> = createRef();

    public configuratorWrapper: Ref<HTMLElement> = createRef();

    connectedCallback(): void {
      this.addEventListener(
        'configurator-context-update',
        this.updateConfiguratorContext,
      );

      this.addEventListener(
        'validation-configurator-context-update',
        this.updateValidationConfiguratorContext,
      );

      this.addEventListener('reset-action-event', () => {
        this.eventToSave = undefined;
      });

      super.connectedCallback();
    }

    disconnectedCallback(): void {
      this.removeEventListener('reset-action-event', () => {
        this.eventToSave = undefined;
      });
    }

    updateValidationConfiguratorContext(
      event: CustomEvent<{ context: ValidationConfiguratorContextType }>,
    ) {
      const { context } = event.detail;

      this.validationConfiguratorContext = context;
    }

    updateConfiguratorContext(
      event: CustomEvent<{ context: ConfiguratorContextType }>,
    ) {
      const { context } = event.detail;

      this.configuratorContext = context;
    }

    async scheduleUpdate() {
      setBaseUrl(this.baseUrl);

      window.i18next = window.i18next || i18nInit(this.locale);
      await window.i18next;

      super.scheduleUpdate();
    }

    async firstUpdated() {
      this.globalContext = {
        layout: this.layout,
        country: this.country,
        locale: this.locale,
        baseUrl: this.baseUrl,
        customerId: this.customerId,
        debtor: this.debtor,
        vatRate: this.vatRate,
        loggedIn: this.loggedIn,
      };

      /**
       * Type casted like this because i need the properties of FgConfigurator, and not he function of ConfiguratorMixin.
       * ConfiguratorMixin is extended by FgConfigurator so it should not be a problem.
       */
      this.configuratorContext = configuratorGlobal.init(
        this as unknown as FgConfigurator,
      );
      this.validationConfiguratorContext = initValidationConfiguratorContext();

      await this.getConfigurator({
        optionParams: {
          configurationId: this.configuratorContext.configurationId,
          color: this.configuratorContext.color,
        },
      });
    }

    protected willUpdate(
      _changedProperties: PropertyValueMap<ConfiguratorMixinClass>,
    ): void {
      if (_changedProperties.has('steps')) {
        this.updateStepValidationObject();
        const hspEnabled = getHspEnabled(this.steps);
        this.configuratorContext = {
          ...this.configuratorContext,
          hspEnabled,
        };
      }

      exposeComponentChange(this);

      const { isMobile } = this.mobileController;

      this.personalisationContext = this.peronsalisationEventController.context;
      this.printExampleContext = this.printExampleEventController.context;

      if (_changedProperties.has('stickySummaryHeight') && !isMobile) {
        this.configuratorContext = {
          ...this.configuratorContext,
          stickySummaryHeight: this.stickySummaryHeight,
        };
      }
    }

    setMode(event: CustomEvent) {
      const { mode } = event.detail;

      this.configuratorContext = {
        ...this.configuratorContext,
        mode: mode,
      };
    }

    updateStepValidationObject() {
      const currentSteps: string[] = this.steps.map(step => step.type);

      Object.keys(this.validSteps).forEach(key => {
        if (!currentSteps.includes(key)) {
          delete this.validSteps[key];
          delete this.validationConfiguratorContext.showValidationMessages[key];
        }
      });

      this.configuratorContext = {
        ...this.configuratorContext,
        hasValidSteps: Object.values(this.validSteps).every(Boolean),
      };
    }

    async getConfigurator({ optionParams }: { optionParams?: ParsedQs } = {}) {
      if (!this.configuratorContext.product) {
        return;
      }

      const timerId = setTimeout(() => {
          // delay loading state to prevent flickering
          this.isConfiguratorLoaded = false;
        }, ConfiguratorMixinClass.LOADINGSPINNER_DELAY),
        { layout: contextLayout, locale } = this.globalContext,
        { product, configurationId } = this.configuratorContext,
        layout =
          contextLayout === 'wholesale'
            ? ConfiguratorLayout.WHOLESALE
            : ConfiguratorLayout.RETAIL;

      this.activeTimeoutIDs.push(timerId);
      const { isMobile } = this.mobileController;
      if (
        this.getGetConfiguratorPromise &&
        !this.getGetConfiguratorPromise.isCancelled
      ) {
        this.getGetConfiguratorPromise.cancel();
      }

      this.getGetConfiguratorPromise = ConfiguratorService.getGetConfigurator({
        code: product,
        locale: locale,
        layout: layout,
        debtor: this.debtor,
        country: this.country,
        ...optionParams,
      });

      try {
        const configuratorData = await this.getGetConfiguratorPromise;
        if (configuratorData) {
          this.isLoading = false;
          this._clearActiveTimeouts();
          this.isConfiguratorLoaded = true;

          const { steps, configuration, unprinted } = configuratorData;

          this.steps = steps;
          this.configuration = configuration;
          this.unprinted = unprinted;
        }
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        this.isLoading = false;
        this.error = true;
      } finally {
        this.getGetConfiguratorPromise = null;
        if (
          Object.keys(this.validationConfiguratorContext.showValidationMessages)
            .length === 0
        ) {
          this.steps?.map(step => {
            this.validationConfiguratorContext =
              updateValidationConfiguratorContext({
                validationConfiguratorContext:
                  this.validationConfiguratorContext,
                step: {
                  name: step.type,
                  value: false,
                },
              });
          });
        }

        if (configurationId && isMobile && !this.toggleMobileConfigurator) {
          this.dispatchEvent(
            new CustomEvent('toggle-configurator', { composed: true }),
          );
          this.toggleConfigurator();
        }
        this.validateMessages();
      }
    }

    onOptionSelected(event: CustomEvent, step: StepDto['type']) {
      const { queryString } = event.detail.option;

      /**
       * Reset configuration ID if it is passed on load, and the configuration quantity is changed. This is to prevent
       * the quantity & price breakdown from not being updated, Which are set via a different API call.
       */
      if (!this.configuratorContext.configurationId) {
        this.configuratorContext = {
          ...this.configuratorContext,
          configurationId: undefined,
        };
      }

      this.validationConfiguratorContext = updateValidationConfiguratorContext({
        validationConfiguratorContext: this.validationConfiguratorContext,
        showValidationSnackbar: false,
        step: {
          name: step,
          value: false,
        },
      });

      this.queryString = queryString;

      this.getUpdatedConfiguration(queryString);
    }

    onDefaultColorSelected() {
      this.configuratorContext = {
        ...this.configuratorContext,
        color: '',
      };
    }

    onQuantitySet(event: CustomEvent) {
      const { queryString } = event.detail;

      /**
       * Reset configuration ID if it is passed on load, and the configuration quantity is changed. This is to prevent
       * the quantity & price breakdown from not being updated, Which are set via a different API call.
       */
      if (this.configuratorContext.configurationId !== '') {
        this.configuratorContext = {
          ...this.configuratorContext,
          configurationId: undefined,
        };
      }

      this.queryString = queryString;
      this.validationConfiguratorContext = updateValidationConfiguratorContext({
        validationConfiguratorContext: this.validationConfiguratorContext,
        showValidationSnackbar: false,
      });

      this.getUpdatedConfiguration(queryString);
    }

    getUpdatedConfiguration(queryString: string) {
      const optionParams = getParamsObjectFromQueryString(queryString);

      this.getConfigurator({
        optionParams: {
          ...optionParams,
          quantities: Object.assign({}, optionParams.quantities),
        },
      });
    }

    resetConfiguration() {
      this.queryString = '';

      this.configuratorContext = {
        ...this.configuratorContext,
        color: undefined,
      };

      goToStep(this.configuratorElement.value, 0);

      this.activeStep = 0;
      this.getUpdatedConfiguration(this.queryString);
      this.resetConfigurator = true;
    }

    private _clearActiveTimeouts() {
      while (this.activeTimeoutIDs.length > 0) {
        const timerId = this.activeTimeoutIDs.pop();
        if (timerId) {
          clearTimeout(timerId);
        }
      }
    }

    validateMessages() {
      this.steps?.map(step => {
        if (step.type === 'quantity_per_variant') {
          this.quantityValidated = step.validationMessage === null;
        }

        Object.assign(this.validSteps, {
          [step.type]: step.validationMessage === null,
        });
      });
    }

    validateQuantity(event: CustomEvent) {
      const { validationMessage } = event.detail;

      this.steps?.map(step => {
        if (ConfiguratorMixinClass.QUANTITY_STEPS.includes(step.type as Step)) {
          step.validationMessage = validationMessage;

          this.validSteps = {
            ...this.validSteps,
            [step.type]: validationMessage === null,
          };

          this.quantityValidated = validationMessage === null;
        }
      });
    }

    /**
     * Custom event triggered in the fg-configurator-quantity-variants, to check if one of the quantity inputs is over
     * the stock limit. This will prevent the add to cart/update cart button form being triggered when the quantity is
     * higher than the stock limit.
     *
     * @param event
     */
    validateStockPerVariant(event: CustomEvent<{ valid: boolean }>) {
      this.quantityValidated =
        event.detail.valid && !Object.values(this.validSteps).includes(false);
    }

    toggleValidationLogic(event: CustomEvent<{ actionEvent: ActionEvent }>) {
      this.toggleAllStepsValidationMessages(event);
    }

    toggleAllStepsValidationMessages(
      event: CustomEvent<{ actionEvent: ActionEvent }>,
    ) {
      this.firstValidationMessage = null;
      this.validationConfiguratorContext = updateValidationConfiguratorContext({
        validationConfiguratorContext: this.validationConfiguratorContext,
        showValidationSnackbar: false,
      });

      /**
       * Double check if quantity is actually validated, if not set the quantity step to false to show the validation
       * for the quantity step.
       */
      if (!this.quantityValidated && this.globalContext.layout === 'retail') {
        Object.keys(this.validSteps).find(key => {
          if (key.includes('quantity'))
            this.validSteps = {
              ...this.validSteps,
              [key]: false,
            };
        });
      }

      const firstInvalidStep = Object.values(this.validSteps).findIndex(
          step => step === false,
        ),
        { actionEvent } = event.detail;

      this.firstValidationMessage = Object.keys(
        this.validationConfiguratorContext.showValidationMessages,
      )[firstInvalidStep];

      if (this.globalContext.layout === 'wholesale') {
        this.validationConfiguratorContext =
          updateValidationConfiguratorContext({
            validationConfiguratorContext: this.validationConfiguratorContext,
            showValidationSnackbar:
              this.steps.find(step => step.type === this.firstValidationMessage)
                ?.validationMessage ?? false,
          });
      }

      Object.keys(this.validSteps).forEach(key => {
        this.validationConfiguratorContext =
          updateValidationConfiguratorContext({
            validationConfiguratorContext: this.validationConfiguratorContext,
            step: {
              name: key,
              value: !this.validSteps[key],
            },
          });
      });

      if (actionEvent !== 'add-to-cart' && actionEvent !== 'update-cart') {
        this.dispatchEvent(
          new CustomEvent('trigger-validation-information', {
            composed: true,
            detail: {
              steps: this.validSteps,
              actionEvent: actionEvent,
            },
          }),
        );
      }
    }

    scrollToValidationMessage(step: StepDto['type']) {
      if (step === this.firstValidationMessage) {
        return true;
      }
      return false;
    }

    resetScrollValidationMessage() {
      this.firstValidationMessage = null;
    }

    async toggleConfigurator() {
      /**
       * wait for the configurator to be rendered completely before toggling the configurator, this prevents the
       * configuratorWrapper to be null
       */
      await this.updateComplete;

      this.toggleMobileConfigurator = toggleMobileConfigurator(
        this.toggleMobileConfigurator,
        this.configuratorWrapper.value,
        this.mobileController.isMobile,
      );
    }
  }

  return ConfiguratorMixinClass;
};
